import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import './MomentumRadar.css';
import './IntradayScan.css';
import RefreshButton from './RefreshButton';
import WatchlistStar from './WatchlistStar';

const MomentumRadar = ({ onBack }) => {
  // Single active feature state
  const [activeFeature, setActiveFeature] = useState('lom'); // 'lom' | 'highlow' | 'spike' | '3percent'

  // Data states for each feature
  const [lomStocks, setLomStocks] = useState([]);
  const [highLowStocks, setHighLowStocks] = useState([]);
  const [spikeStocks, setSpikeStocks] = useState([]);
  const [threePercentData, setThreePercentData] = useState({ topPerformers: [], lowPerformers: [] });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter states
  const [trend, setTrend] = useState(''); // '' | 'high' | 'low'

  // Sub-filter states for each feature
  const [lomTimeframe, setLomTimeframe] = useState('5min');
  const [lomTrend, setLomTrend] = useState('high');
  const [spikeTimeframe, setSpikeTimeframe] = useState('5min');
  const [spikeTrend, setSpikeTrend] = useState('high');
  const [highLowDirection, setHighLowDirection] = useState('ALL');
  const [threePercentDirection, setThreePercentDirection] = useState('ALL');

  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('darkmode'));
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => observer.disconnect();
  }, []);

  const formatTime = (datetime) => {
    if (!datetime) return "";
    const d = new Date(datetime);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatNumber = useCallback((num) => {
    if (num === null || num === undefined) return 'N/A';
    const number = Number(num);
    return isNaN(number) ? 'N/A' : number.toFixed(2);
  }, []);

  // Fetch Short Term LOM Data
  const fetchLomData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/short-term-lom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timeframe: lomTimeframe === '5min' ? '5min' : '15min',
          trend: lomTrend,
          category: 'fo'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch short term LOM data');
      }

      const result = await response.json();
      // Handle multiple response formats
      let data = [];
      if (Array.isArray(result.data)) {
        data = result.data;
      } else if (result.status === 'success' && Array.isArray(result)) {
        data = result;
      }
      setLomStocks(data);
    } catch (err) {
      console.error('Error fetching short term LOM data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [lomTimeframe, lomTrend]);

  // Fetch High/Low Data
  const fetchHighLowData = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 2000;
    
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_BASE_URL}/breakouts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ stock_list: [] })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || `Server error: ${response.status}`);
      }
      
      if (responseData && Array.isArray(responseData.data)) {
        setHighLowStocks(responseData.data);
      } else {
        throw new Error('Invalid data format received from server');
      }
    } catch (err) {
      if (retryCount < MAX_RETRIES && 
          (err.message.includes('Network') || err.message.includes('Failed to fetch'))) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return fetchHighLowData(retryCount + 1);
      }
      setError(`Failed to load data: ${err.message}`);
      setHighLowStocks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Momentum Spike Data
  const fetchSpikeData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/stockanalyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          timeframe: spikeTimeframe,
          trend: spikeTrend,
          count: 50
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, ${errorText}`);
      }

      const data = await response.json();
      if (data && Array.isArray(data.data) && data.data.length > 0) {
        setSpikeStocks(data.data);
      } else {
        setSpikeStocks([]);
      }
    } catch (err) {
      console.error('Error in fetchData:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [spikeTimeframe, spikeTrend]);

  // Fetch 3% Rule Data
  const fetchThreePercentData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/breakout-three-percent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'fo' })
      });
      
      const data = await response.json();
      
      // Handle multiple response formats for top/low performers
      let topPerformers = [];
      let lowPerformers = [];
      
      if (data.data && typeof data.data === 'object') {
        topPerformers = Array.isArray(data.data.top_performers) ? data.data.top_performers : [];
        lowPerformers = Array.isArray(data.data.low_performers) ? data.data.low_performers : [];
      } else {
        topPerformers = Array.isArray(data.top_performers) ? data.top_performers : [];
        lowPerformers = Array.isArray(data.low_performers) ? data.low_performers : [];
      }
      
      setThreePercentData({
        topPerformers: topPerformers,
        lowPerformers: lowPerformers
      });
    } catch (err) {
      console.error('Error fetching three percent data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data when active feature changes
  useEffect(() => {
    switch (activeFeature) {
      case 'lom':
        fetchLomData();
        break;
      case 'highlow':
        fetchHighLowData();
        break;
      case 'spike':
        fetchSpikeData();
        break;
      case '3percent':
        fetchThreePercentData();
        break;
      default:
        break;
    }
  }, [activeFeature, fetchLomData, fetchHighLowData, fetchSpikeData, fetchThreePercentData]);

  // Refresh handler
  const handleRefresh = () => {
    switch (activeFeature) {
      case 'lom':
        fetchLomData();
        break;
      case 'highlow':
        fetchHighLowData();
        break;
      case 'spike':
        fetchSpikeData();
        break;
      case '3percent':
        fetchThreePercentData();
        break;
      default:
        break;
    }
  };

  // Filtered data for High/Low
  const filteredHighLowStocks = useMemo(() => {
    let list = highLowStocks;
    if (highLowDirection === "UP") list = list.filter((s) => s.chart === "up");
    else if (highLowDirection === "DOWN") list = list.filter((s) => s.chart === "down");
    return list;
  }, [highLowStocks, highLowDirection]);

  // Filtered data for 3% Rule
  const filteredThreePercent = useMemo(() => {
    const top = threePercentData.topPerformers.filter(stock => stock.percentage_change >= 0);
    const low = threePercentData.lowPerformers.filter(stock => stock.percentage_change < 0);
    return {
      showTop: threePercentDirection === 'ALL' || threePercentDirection === 'UP',
      showLow: threePercentDirection === 'ALL' || threePercentDirection === 'DOWN',
      top: top,
      low: low
    };
  }, [threePercentData, threePercentDirection]);

  // Render sub-filters based on active feature
  const renderSubFilters = () => {
    switch (activeFeature) {
      case 'lom':
        return (
          <div className="sub-filters">
            <div className="filter-group">
              <span>Timeframe:</span>
              <button 
                className={`sub-filter-btn ${lomTimeframe === '5min' ? 'active' : ''}`}
                onClick={() => setLomTimeframe('5min')}
              >
                5 Min
              </button>
              <button 
                className={`sub-filter-btn ${lomTimeframe === '15min' ? 'active' : ''}`}
                onClick={() => setLomTimeframe('15min')}
              >
                15 Min
              </button>
            </div>
            <div className="filter-group">
              <span>Trend:</span>
              <button 
                className={`sub-filter-btn ${lomTrend === 'high' ? 'active' : ''}`}
                onClick={() => setLomTrend('high')}
              >
                UP
              </button>
              <button 
                className={`sub-filter-btn ${lomTrend === 'low' ? 'active' : ''}`}
                onClick={() => setLomTrend('low')}
              >
                DOWN
              </button>
            </div>
          </div>
        );
      case 'highlow':
        return (
          <div className="sub-filters">
            <span>Direction:</span>
            <button 
              className={`sub-filter-btn ${highLowDirection === 'UP' ? 'active' : ''}`}
              onClick={() => setHighLowDirection('UP')}
            >
              UP
            </button>
            <button 
              className={`sub-filter-btn ${highLowDirection === 'DOWN' ? 'active' : ''}`}
              onClick={() => setHighLowDirection('DOWN')}
            >
              DOWN
            </button>
            <button 
              className={`sub-filter-btn ${highLowDirection === 'ALL' ? 'active' : ''}`}
              onClick={() => setHighLowDirection('ALL')}
            >
              ALL
            </button>
          </div>
        );
      case 'spike':
        return (
          <div className="sub-filters">
            <div className="filter-group">
              <span>Timeframe:</span>
              <button 
                className={`sub-filter-btn ${spikeTimeframe === '5min' ? 'active' : ''}`}
                onClick={() => setSpikeTimeframe('5min')}
              >
                5 Min
              </button>
              <button 
                className={`sub-filter-btn ${spikeTimeframe === '15min' ? 'active' : ''}`}
                onClick={() => setSpikeTimeframe('15min')}
              >
                15 Min
              </button>
            </div>
            <div className="filter-group">
              <span>Trend:</span>
              <button 
                className={`sub-filter-btn ${spikeTrend === 'high' ? 'active' : ''}`}
                onClick={() => setSpikeTrend('high')}
              >
                UP
              </button>
              <button 
                className={`sub-filter-btn ${spikeTrend === 'low' ? 'active' : ''}`}
                onClick={() => setSpikeTrend('low')}
              >
                DOWN
              </button>
            </div>
          </div>
        );
      case '3percent':
        return (
          <div className="sub-filters">
            <span>Filter:</span>
            <button 
              className={`sub-filter-btn ${threePercentDirection === 'UP' ? 'active' : ''}`}
              onClick={() => setThreePercentDirection('UP')}
            >
              UP
            </button>
            <button 
              className={`sub-filter-btn ${threePercentDirection === 'DOWN' ? 'active' : ''}`}
              onClick={() => setThreePercentDirection('DOWN')}
            >
              DOWN
            </button>
            <button 
              className={`sub-filter-btn ${threePercentDirection === 'ALL' ? 'active' : ''}`}
              onClick={() => setThreePercentDirection('ALL')}
            >
              ALL
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // Skeleton loading for tables
  const renderSkeletonTable = useCallback(() => {
    return (
      <div className="intraday-table-container skeleton-table">
        <div className="loading-container" style={{ padding: '40px', textAlign: 'center' }}>
          <div className="loading-circle"></div>
        </div>
      </div>
    );
  }, []);

  // Render table based on active feature
  const renderTable = () => {
    if (loading) {
      return renderSkeletonTable();
    }

    if (error) {
      return <div className="error-message">{error}</div>;
    }

    switch (activeFeature) {
      case 'lom':
        return (
          <div className="intraday-table-container lom-table-container">
            <div className="high-turnover-table">
              <table>
                <colgroup>
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '25%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>LOM Time</th>
                    <th>% Change</th>
                    <th>LTP</th>
                  </tr>
                </thead>
                <tbody>
                  {lomStocks.length > 0 ? (
                    lomStocks.map((stock, index) => {
                      const lomTime = new Date(stock.end_time);
                      const formattedTime = lomTime.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true
                      });
                      const percentChange = Number(stock.percent_change) || 0;
                      const isPositive = percentChange >= 0;
                      const formattedPercent = (isPositive ? '+' : '') + percentChange.toFixed(2) + '%';
                      const formattedLTP = typeof stock.ltp === 'number' 
                        ? stock.ltp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : stock.ltp || 'N/A';
                      
                      return (
                        <tr key={index}>
                          <td style={{ textAlign: 'left' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <WatchlistStar 
                                stock={{
                                  symbol: stock.symbol,
                                  name: stock.symbol
                                }} 
                              />
                              {stock.symbol}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>{formattedTime}</td>
                          <td style={{ textAlign: 'center' }} className={isPositive ? 'positive' : 'negative'}>
                            {formattedPercent}
                          </td>
                          <td style={{ textAlign: 'center' }}>{formattedLTP}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="no-data" style={{ textAlign: 'center', padding: '20px' }}>No data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'highlow':
        return (
          <div className="intraday-table-container highlow-table-container">
            <div className="high-turnover-table">
              <table>
                <colgroup>
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '15%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>⭐</th>
                    <th>Name</th>
                    <th>Date & Time</th>
                    <th>Percentage</th>
                    <th>After B.O</th>
                    <th>SIGNALS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHighLowStocks.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="no-data" style={{ textAlign: 'center', padding: '20px' }}>No results</td>
                    </tr>
                  ) : (
                    filteredHighLowStocks.map((stock) => (
                      <tr key={stock.id}>
                        <td style={{ textAlign: 'center' }}>
                          <WatchlistStar 
                            stock={{
                              symbol: stock.symbol,
                              name: stock.symbol
                            }} 
                            size="18px"
                          />
                        </td>
                        <td style={{ textAlign: 'left' }}>{stock.symbol}</td>
                        <td style={{ textAlign: 'center' }}>{stock.breakout_time}</td>
                        <td style={{ textAlign: 'center' }} className={stock.breakout_pct && !String(stock.breakout_pct).includes('-') ? 'positive' : 'negative'}>
                          {stock.breakout_pct !== "" ? `${String(stock.breakout_pct).includes('-') ? '' : '+'}${stock.breakout_pct}%` : "—"}
                        </td>
                        <td style={{ textAlign: 'center' }} className={stock.after_bo && !String(stock.after_bo).includes('-') ? 'positive' : 'negative'}>
                          {stock.after_bo !== "" ? `${String(stock.after_bo).includes('-') ? '' : '+'}${stock.after_bo}%` : "—"}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {stock.chart === "up" ? (
                            <span style={{ color: '#10b981', fontSize: '1.2rem', fontWeight: 'bold' }}>↑</span>
                          ) : stock.chart === "down" ? (
                            <span style={{ color: '#ef4444', fontSize: '1.2rem', fontWeight: 'bold' }}>↓</span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'spike':
        return (
          <div className="intraday-table-container spike-table-container">
            <div className="high-turnover-table">
              <table>
                <colgroup>
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>SYMBOL</th>
                    <th>LTP</th>
                    <th>CHANGE %</th>
                    <th>LOM %</th>
                    <th>TIME</th>
                    <th>SIGNALS</th>
                  </tr>
                </thead>
                <tbody>
                  {spikeStocks && spikeStocks.length > 0 ? (
                    [...spikeStocks]
                      .sort((a, b) => (b["Change (%)"] || 0) - (a["Change (%)"] || 0))
                      .map((stock, index) => {
                        const change = stock["Change (%)"] || 0;
                        const lom = stock["LOM (%)"] || 0;
                        const ltp = stock["LTP"] || 0;
                        const stockName = stock["Stock"] || 'N/A';
                        const lomTime = stock["LOM Time"];

                        return (
                          <tr key={`${stockName}-${index}`}>
                            <td style={{ textAlign: 'left' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <WatchlistStar 
                                  stock={{
                                    symbol: stockName,
                                    name: stockName
                                  }}
                                />
                                {stockName}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>{formatNumber(ltp)}</td>
                            <td style={{ textAlign: 'center' }} className={change >= 0 ? 'positive' : 'negative'}>
                              {formatNumber(change)}%
                            </td>
                            <td style={{ textAlign: 'center' }} className={lom >= 0 ? 'positive' : 'negative'}>
                              {formatNumber(lom)}%
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {lomTime ? new Date(lomTime).toLocaleTimeString() : 'N/A'}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span 
                                style={{
                                  fontSize: '1.2rem',
                                  fontWeight: 'bold',
                                  color: change >= 0 ? '#10b981' : '#ef4444'
                                }}
                              >
                                {change >= 0 ? '↑' : '↓'}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case '3percent':
        return (
          <div className="rule-strategy-container">
            <div className="results-count" style={{ marginBottom: '15px', color: 'var(--text-color)', fontWeight: '600' }}>
              Showing {filteredThreePercent.top.length + filteredThreePercent.low.length} results ({threePercentDirection.toUpperCase()})
            </div>
            
            {filteredThreePercent.showTop && filteredThreePercent.top.length > 0 && (
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#10b981' }}>Top Performers ({filteredThreePercent.top.length})</h3>
                <div className="intraday-table-container three-percent-table-container">
                  <div className="high-turnover-table">
                    <table>
                      <colgroup>
                        <col style={{ width: '35%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '25%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Stock</th>
                          <th>LTP</th>
                          <th>% Change</th>
                          <th>Proximity to High</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredThreePercent.top.map((stock, i) => (
                          <tr key={i}>
                            <td style={{ textAlign: 'left' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <WatchlistStar stock={stock} size="16px" />
                                {stock.stock}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>{stock.ltp}</td>
                            <td style={{ textAlign: 'center' }} className="positive">{stock.percentage_change}%</td>
                            <td style={{ textAlign: 'center' }}>{stock.proximity_to_high}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            
            {filteredThreePercent.showLow && filteredThreePercent.low.length > 0 && (
              <div>
                <h3 style={{ margin: '0 0 10px 0', color: '#ef4444' }}>Low Performers ({filteredThreePercent.low.length})</h3>
                <div className="intraday-table-container three-percent-table-container">
                  <div className="high-turnover-table">
                    <table>
                      <colgroup>
                        <col style={{ width: '35%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '25%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Stock</th>
                          <th>LTP</th>
                          <th>% Change</th>
                          <th>Proximity to Low</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredThreePercent.low.map((stock, i) => (
                          <tr key={i}>
                            <td style={{ textAlign: 'left' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <WatchlistStar stock={stock} size="16px" />
                                {stock.stock}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>{stock.ltp}</td>
                            <td style={{ textAlign: 'center' }} className="negative">{stock.percentage_change}%</td>
                            <td style={{ textAlign: 'center' }}>{stock.proximity_to_low}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            
            {(!filteredThreePercent.showTop || filteredThreePercent.top.length === 0) && 
             (!filteredThreePercent.showLow || filteredThreePercent.low.length === 0) && (
              <div className="table-empty-state" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                No stocks match "{threePercentDirection}" filter
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Get feature title
  const getFeatureTitle = () => {
    switch (activeFeature) {
      case 'lom': return `LOSS OF MOMENTUM (${lomTrend.toUpperCase()} - ${lomTimeframe})`;
      case 'highlow': return '2 DAY HIGH & LOW';
      case 'spike': return `Momentum Spike (${spikeTrend.toUpperCase()} - ${spikeTimeframe})`;
      case '3percent': return '3% RULE STRATEGY';
      default: return 'Momentum Radar';
    }
  };

  return (
    <div className="intraday-page momentum-radar-page">
      <header className="intraday-header">
        <div className="intraday-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
          </div>
          <div className="intraday-user">
            <RefreshButton 
              onRefresh={handleRefresh} 
              isLoading={loading} 
              size="small"
              className="mr-2"
            />
          </div>
        </div>
      </header>

      <main className="intraday-main">
        <div className="intraday-top">
          <div className="s4u-title-row">
            <div className="s4u-page-heading intraday-heading-override">
              <h1 className="page-title intraday-glowing-title">
                {getFeatureTitle()} <span className="info-icon">ℹ️</span>
              </h1>
              <button className="s4u-back-btn intraday-back-btn" onClick={onBack}>← Back to Dashboard</button>
            </div>
          </div>
        </div>

        {/* Feature Categories - Horizontal scrollable on mobile */}
        <div className="spike-sections-container categories" style={{ marginBottom: '15px' }}>
          <div className="spike-section">
            <button 
              className={`toggle-design-btn ${activeFeature === 'lom' ? 'active' : ''}`}
              onClick={() => setActiveFeature('lom')}
            >
              LOSS OF MOMENTUM (LOM)
            </button>
          </div>
          
          <div className="spike-section">
            <button 
              className={`toggle-design-btn ${activeFeature === 'highlow' ? 'active' : ''}`}
              onClick={() => setActiveFeature('highlow')}
            >
              Switch 2 DAY HIGH & LOW
            </button>
          </div>
          
          <div className="spike-section">
            <button 
              className={`toggle-design-btn ${activeFeature === 'spike' ? 'active' : ''}`}
              onClick={() => setActiveFeature('spike')}
            >
              5 & 15 min Momentum Spike
            </button>
          </div>
          
          <div className="spike-section">
            <button 
              className={`toggle-design-btn ${activeFeature === '3percent' ? 'active' : ''}`}
              onClick={() => setActiveFeature('3percent')}
            >
              3% Rule
            </button>
          </div>
        </div>

        {/* Sub-filters for each feature */}
        <div style={{ marginBottom: '20px' }}>
          {renderSubFilters()}
        </div>

        {/* Top filter buttons (if needed, kept for compatibility) */}
        {trend !== undefined && (
          <div className="filter-buttons" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-color)' }}>Filter By:</h3>
            <button 
              className={`sub-filter-btn ${trend === 'high' ? 'active' : ''}`} 
              onClick={() => setTrend('high')}
            >UP</button>
            <button 
              className={`sub-filter-btn ${trend === 'low' ? 'active' : ''}`}
              onClick={() => setTrend('low')}
            >DOWN</button>
            {trend && (
              <button 
                className="sub-filter-btn clear"
                onClick={() => setTrend('')}
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Table Content */}
        {renderTable()}
      </main>
    </div>
  );
};

export default MomentumRadar;

